import {
  LayoutDashboard, Users, Building2, UserRound, Wallet, Landmark,
  ArrowLeftRight, BookOpen, Settings2, Coins, Bitcoin, ArrowDownUp,
  KeyRound, PlugZap, Plug, ListTree, Cpu,
} from "lucide-react";

// Navigation, filtered per role
export const NAV = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "staff", "company", "customer"] },
  { path: "/users", label: "Users & Roles", icon: Users, roles: ["admin", "staff"] },
  { path: "/companies", label: "Companies", icon: Building2, roles: ["admin", "staff"] },
  { path: "/customers", label: "Customers", icon: UserRound, roles: ["admin", "staff", "company"] },
  { path: "/accounts", label: "Accounts", icon: Landmark, roles: ["admin", "staff", "company", "customer"] },
  { path: "/wallets", label: "Wallets", icon: Wallet, roles: ["admin", "staff", "company", "customer"] },
  { path: "/transactions", label: "Transactions", icon: ArrowLeftRight, roles: ["admin", "staff", "company", "customer"] },
  { path: "/ledger", label: "Ledger", icon: BookOpen, roles: ["admin", "staff", "company", "customer"] },
  { path: "/ledger-accounts", label: "Chart of Accounts", icon: ListTree, roles: ["admin", "staff", "company", "customer"] },
  { path: "/services", label: "Services", icon: Settings2, roles: ["admin", "staff", "company", "customer"] },
  { path: "/currencies", label: "Currencies", icon: Coins, roles: ["admin", "staff", "company", "customer"] },
  { path: "/crypto-wallets", label: "Crypto Wallets", icon: Bitcoin, roles: ["admin", "staff", "company", "customer"] },
  { path: "/crypto-transactions", label: "Crypto Txns", icon: ArrowDownUp, roles: ["admin", "staff", "company", "customer"] },
  { path: "/keys", label: "API Keys", icon: KeyRound, roles: ["admin", "staff", "company", "customer"] },
  { path: "/processors", label: "Processors", icon: PlugZap, roles: ["admin", "staff"] },
  { path: "/processor-engine", label: "Processor Engine", icon: Cpu, roles: ["admin", "staff"] },
  { path: "/integrations", label: "Integrations", icon: Plug, roles: ["admin", "staff"] },
];

export const CURRENCY_OPTIONS = [];

// Resource definitions used by ResourcePage
export const RESOURCES = {
  users: {
    endpoint: "/users",
    title: "Users & Roles",
    subtitle: "Manage operators, companies and customers with enforced role permissions",
    createRoles: ["admin"],
    idKey: "id",
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email", mono: true },
      { key: "role", label: "Role", render: "roleBadge" },
      { key: "totp_enabled", label: "2FA", render: "bool" },
      { key: "created_at", label: "Created", render: "date" },
    ],
    fields: [
      { name: "name", label: "Full Name", type: "text", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "password", label: "Password", type: "password", required: true, hint: "Min 8 characters" },
      { name: "role", label: "Role", type: "select", options: [
        { value: "admin", label: "Admin" }, { value: "staff", label: "Staff" },
        { value: "company", label: "Company" }, { value: "customer", label: "Customer" }] },
      { name: "company_id", label: "Company", type: "select", source: "companies", optional: true },
      { name: "customer_id", label: "Customer", type: "select", source: "customers", optional: true },
    ],
  },
  companies: {
    endpoint: "/companies",
    title: "Companies",
    subtitle: "Business entities operating on the datacenter",
    createRoles: ["admin", "staff"],
    columns: [
      { key: "name", label: "Company" },
      { key: "email", label: "Email", mono: true },
      { key: "phone", label: "Phone" },
      { key: "country", label: "Country" },
      { key: "status", label: "Status", render: "statusBadge" },
    ],
    fields: [
      { name: "name", label: "Company Name", type: "text", required: true },
      { name: "email", label: "Email", type: "email" },
      { name: "phone", label: "Phone", type: "text" },
      { name: "country", label: "Country", type: "text" },
      { name: "status", label: "Status", type: "select", options: [
        { value: "active", label: "Active" }, { value: "suspended", label: "Suspended" }] },
    ],
  },
  customers: {
    endpoint: "/customers",
    title: "Customers",
    subtitle: "End customers under companies",
    createRoles: ["admin", "staff", "company"],
    columns: [
      { key: "name", label: "Customer" },
      { key: "email", label: "Email", mono: true },
      { key: "phone", label: "Phone" },
      { key: "status", label: "Status", render: "statusBadge" },
    ],
    fields: [
      { name: "name", label: "Customer Name", type: "text", required: true },
      { name: "email", label: "Email", type: "email" },
      { name: "phone", label: "Phone", type: "text" },
      { name: "company_id", label: "Company", type: "select", source: "companies", optional: true },
      { name: "status", label: "Status", type: "select", options: [
        { value: "active", label: "Active" }, { value: "suspended", label: "Suspended" }] },
    ],
  },
  accounts: {
    endpoint: "/accounts",
    title: "Accounts",
    subtitle: "Ledger accounts with live running balances",
    createRoles: ["admin", "staff"],
    columns: [
      { key: "name", label: "Account" },
      { key: "owner_type", label: "Owner", render: "capBadge" },
      { key: "currency", label: "Ccy", mono: true },
      { key: "balance", label: "Balance", render: "amount" },
      { key: "status", label: "Status", render: "statusBadge" },
    ],
    fields: [
      { name: "name", label: "Account Name", type: "text", required: true },
      { name: "owner_type", label: "Owner Type", type: "select", options: [
        { value: "company", label: "Company" }, { value: "customer", label: "Customer" }] },
      { name: "owner_id", label: "Owner", type: "ownerSelect", required: true },
      { name: "currency", label: "Currency", type: "select", source: "currencies", valueKey: "code", labelKey: "code" },
      { name: "balance", label: "Opening Balance", type: "number" },
    ],
  },
  wallets: {
    endpoint: "/wallets",
    title: "Wallets",
    subtitle: "Fiat & crypto wallets with balances",
    createRoles: ["admin", "staff"],
    columns: [
      { key: "currency", label: "Ccy", mono: true },
      { key: "type", label: "Type", render: "capBadge" },
      { key: "owner_type", label: "Owner", render: "capBadge" },
      { key: "address", label: "Address", mono: true },
      { key: "balance", label: "Balance", render: "amount" },
    ],
    fields: [
      { name: "owner_type", label: "Owner Type", type: "select", options: [
        { value: "company", label: "Company" }, { value: "customer", label: "Customer" }] },
      { name: "owner_id", label: "Owner", type: "ownerSelect", required: true },
      { name: "type", label: "Wallet Type", type: "select", options: [
        { value: "fiat", label: "Fiat" }, { value: "crypto", label: "Crypto" }] },
      { name: "currency", label: "Currency", type: "select", source: "currencies", valueKey: "code", labelKey: "code" },
      { name: "address", label: "Address", type: "text", optional: true },
      { name: "balance", label: "Balance", type: "number" },
    ],
  },
  services: {
    endpoint: "/services",
    title: "Services Catalog",
    subtitle: "Configure fees, limits and availability",
    createRoles: ["admin"],
    toggle: { field: "enabled", endpoint: "/services" },
    columns: [
      { key: "name", label: "Service" },
      { key: "category", label: "Category", render: "capBadge" },
      { key: "code", label: "Code", mono: true },
      { key: "fee_percent", label: "Fee %", render: "pct" },
      { key: "fee_flat", label: "Flat Fee", render: "amount" },
      { key: "min_amount", label: "Min", render: "amount" },
      { key: "max_amount", label: "Max", render: "amount" },
      { key: "enabled", label: "Enabled", render: "toggle" },
    ],
    fields: [
      { name: "name", label: "Service Name", type: "text", required: true },
      { name: "code", label: "Code", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: [
        { value: "Payments & Transfers", label: "Payments & Transfers" },
        { value: "Deposits & Withdrawals", label: "Deposits & Withdrawals" },
        { value: "Cards", label: "Cards" },
        { value: "Banking & Accounts", label: "Banking & Accounts" },
        { value: "Crypto & Digital Assets", label: "Crypto & Digital Assets" },
        { value: "FX & Currency", label: "FX & Currency" },
        { value: "Lending & Credit", label: "Lending & Credit" },
        { value: "Bill Payments & Merchant", label: "Bill Payments & Merchant" },
        { value: "Savings & Investments", label: "Savings & Investments" },
        { value: "Compliance & Value-added", label: "Compliance & Value-added" },
        { value: "On-Ramp & Off-Ramp", label: "On-Ramp & Off-Ramp" },
        { value: "Core Services", label: "Core Services" },
        { value: "Card Networks", label: "Card Networks" },
        { value: "Bank Rails", label: "Bank Rails" },
        { value: "Crypto Assets", label: "Crypto Assets" },
        { value: "Payment Processors", label: "Payment Processors" },
        { value: "Mobile Money", label: "Mobile Money" },
        { value: "Digital Wallets", label: "Digital Wallets" },
        { value: "Settlement & Clearing", label: "Settlement & Clearing" },
        { value: "Blockchain & Crypto Ops", label: "Blockchain & Crypto Ops" },
        { value: "General", label: "General" }] },
      { name: "description", label: "Description", type: "text" },
      { name: "fee_percent", label: "Fee %", type: "number" },
      { name: "fee_flat", label: "Flat Fee", type: "number" },
      { name: "min_amount", label: "Min Amount", type: "number" },
      { name: "max_amount", label: "Max Amount", type: "number" },
    ],
  },
  currencies: {
    endpoint: "/currencies",
    title: "Currencies",
    subtitle: "Fiat and crypto currencies supported side by side",
    createRoles: ["admin"],
    toggle: { field: "enabled", endpoint: "/currencies" },
    columns: [
      { key: "code", label: "Code", mono: true },
      { key: "name", label: "Name" },
      { key: "symbol", label: "Symbol" },
      { key: "type", label: "Type", render: "capBadge" },
      { key: "exchange_rate", label: "Rate (USD)", mono: true },
      { key: "enabled", label: "Enabled", render: "toggle" },
    ],
    fields: [
      { name: "code", label: "Code", type: "text", required: true },
      { name: "name", label: "Name", type: "text", required: true },
      { name: "symbol", label: "Symbol", type: "text", required: true },
      { name: "type", label: "Type", type: "select", options: [
        { value: "fiat", label: "Fiat" }, { value: "crypto", label: "Crypto" }] },
      { name: "decimals", label: "Decimals", type: "number" },
      { name: "exchange_rate", label: "Exchange Rate (to USD)", type: "number" },
    ],
  },
  "crypto-wallets": {
    endpoint: "/crypto-wallets",
    title: "Crypto Wallets",
    subtitle: "On-chain wallet records with balances",
    createRoles: ["admin", "staff"],
    columns: [
      { key: "currency", label: "Asset", mono: true },
      { key: "network", label: "Network" },
      { key: "address", label: "Address", mono: true },
      { key: "owner_type", label: "Owner", render: "capBadge" },
      { key: "balance", label: "Balance", render: "amountRaw" },
      { key: "has_private_key", label: "Private Key", render: "lockedSecret" },
    ],
    fields: [
      { name: "owner_type", label: "Owner Type", type: "select", options: [
        { value: "company", label: "Company" }, { value: "customer", label: "Customer" }] },
      { name: "owner_id", label: "Owner", type: "ownerSelect", required: true },
      { name: "currency", label: "Asset", type: "select", source: "currencies", valueKey: "code", labelKey: "code" },
      { name: "network", label: "Network", type: "text" },
      { name: "address", label: "Address", type: "text", required: true },
      { name: "balance", label: "Balance", type: "number" },
      { name: "private_key", label: "Private Key", type: "password", optional: true, hint: "Stored encrypted · masked after save" },
    ],
  },
  "ledger-accounts": {
    endpoint: "/ledger-accounts",
    title: "Chart of Accounts",
    subtitle: "General ledger accounts by code and category",
    createRoles: ["admin"],
    sortBy: "code",
    columns: [
      { key: "code", label: "Code", mono: true },
      { key: "name", label: "Account Name" },
      { key: "category", label: "Category", render: "capBadge" },
      { key: "status", label: "Status", render: "statusBadge" },
    ],
    fields: [
      { name: "name", label: "Account Name", type: "text", required: true },
      { name: "code", label: "Code", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: [
        { value: "Assets", label: "Assets" }, { value: "Liabilities", label: "Liabilities" },
        { value: "Equity", label: "Equity" }, { value: "Income", label: "Income" }, { value: "Expenses", label: "Expenses" }] },
      { name: "description", label: "Description", type: "text", optional: true },
      { name: "status", label: "Status", type: "select", options: [
        { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
    ],
  },
  "crypto-transactions": {
    endpoint: "/crypto-transactions",
    title: "Crypto Transactions",
    subtitle: "Log of on-chain deposits & withdrawals",
    createRoles: ["admin", "staff"],
    columns: [
      { key: "type", label: "Type", render: "capBadge" },
      { key: "currency", label: "Asset", mono: true },
      { key: "amount", label: "Amount", render: "amountRaw" },
      { key: "network", label: "Network", render: "capBadge" },
      { key: "tx_hash", label: "Tx Hash", render: "txHashLink" },
      { key: "status", label: "Status", render: "statusBadge" },
      { key: "created_at", label: "Time", render: "date" },
      { key: "id", label: "Details", render: "cryptoDetails" },
    ],
    fields: [
      { name: "wallet_id", label: "Crypto Wallet", type: "select", source: "crypto-wallets", valueKey: "id", labelKey: "address" },
      { name: "type", label: "Type", type: "select", options: [
        { value: "deposit", label: "Deposit" }, { value: "withdrawal", label: "Withdrawal" }] },
      { name: "currency", label: "Asset", type: "select", source: "currencies", valueKey: "code", labelKey: "code" },
      { name: "amount", label: "Amount", type: "number", required: true },
      { name: "tx_hash", label: "Tx Hash", type: "text", optional: true },
      { name: "status", label: "Status", type: "select", options: [
        { value: "pending", label: "Pending" }, { value: "confirmed", label: "Confirmed" }, { value: "failed", label: "Failed" }] },
    ],
  },
  "api-keys": {
    endpoint: "/api-keys",
    title: "API Keys",
    subtitle: "Secure store for keys used by external processors & CITIDAN apps",
    createRoles: ["admin", "staff", "company", "customer"],
    columns: [
      { key: "name", label: "Name" },
      { key: "key_prefix", label: "Key Prefix", mono: true },
      { key: "scopes", label: "Permissions", render: "capBadge" },
      { key: "rate_limit_per_min", label: "Rate Limit", render: "rateUsage" },
      { key: "usage_count", label: "Calls", mono: true },
      { key: "endpoint_url", label: "Endpoint URL", render: "url" },
      { key: "secret_key", label: "Secret Key", render: "secretMasked" },
      { key: "status", label: "Status", render: "statusBadge" },
      { key: "created_at", label: "Created", render: "date" },
    ],
    fields: [
      { name: "name", label: "Key Name", type: "text", required: true },
      { name: "scopes", label: "Permissions", type: "select", options: [
        { value: "read", label: "Read" }, { value: "write", label: "Read + Write" }, { value: "admin", label: "Admin (full)" }] },
      { name: "rate_limit_per_min", label: "Rate Limit (req/min)", type: "number" },
      { name: "endpoint_url", label: "Endpoint URL", type: "text", optional: true, hint: "Optional — your callback/base URL" },
      { name: "secret_key", label: "Secret Key", type: "password", optional: true, hint: "Optional — stored securely" },
    ],
  },
  processors: {
    endpoint: "/processors",
    title: "Payment Processors",
    subtitle: "Register processors and add their keys to enable live charging",
    createRoles: ["admin"],
    toggle: { field: "status", endpoint: "/processors", on: "active", off: "inactive" },
    columns: [
      { key: "name", label: "Processor" },
      { key: "type", label: "Type", render: "capBadge" },
      { key: "mode", label: "Mode", render: "capBadge" },
      { key: "status", label: "Status", render: "processorStatus" },
    ],
    fields: [
      { name: "name", label: "Processor Name", type: "text", required: true },
      { name: "type", label: "Type", type: "select", options: [
        { value: "card", label: "Card" }, { value: "bank", label: "Bank" },
        { value: "crypto", label: "Crypto Node" }, { value: "wallet", label: "Wallet" }] },
      { name: "mode", label: "Mode", type: "select", options: [
        { value: "sandbox", label: "Sandbox" }, { value: "live", label: "Live" }] },
    ],
  },
};
